<template>
    <div class="min-h-screen bg-gray-100 p-6">
        <div class="max-w-4xl mx-auto">
            <!-- Header -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-800">Nouvelle Commande</h1>
                        <p class="text-gray-600">Table {{ tableNumber }}</p>
                    </div>
                    <router-link
                            to="/serveur/tables"
                            class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                    >
                        Retour aux tables
                    </router-link>
                </div>
            </div>

            <!-- Products Selection -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 class="text-xl font-semibold mb-4">Produits disponibles</h2>

                <!-- Product Categories -->
                <div class="flex space-x-4 mb-6">
                    <button
                            v-for="category in categories"
                            :key="category"
                            @click="selectedCategory = category"
                            class="px-4 py-2 rounded transition-colors"
                            :class="selectedCategory === category
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'"
                    >
                        {{ category === 'bar' ? 'Bar' : 'Cuisine' }}
                    </button>
                </div>

                <!-- Products Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div
                            v-for="product in filteredProducts"
                            :key="product.id"
                            class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                            @click="addToOrder(product)"
                    >
                        <h3 class="font-semibold text-lg">{{ product.name }}</h3>
                        <p class="text-gray-600">{{ product.price }}€</p>
                        <p class="text-sm text-gray-500">Stock: {{ product.stock }}</p>
                    </div>
                </div>
            </div>

            <!-- Current Order -->
            <div class="bg-white rounded-lg shadow-md p-6">
                <h2 class="text-xl font-semibold mb-4">Commande en cours</h2>

                <div v-if="currentOrderItems.length === 0" class="text-gray-500 text-center py-8">
                    Aucun produit ajouté à la commande
                </div>

                <div v-else class="space-y-3">
                    <div
                            v-for="item in currentOrderItems"
                            :key="item.id"
                            class="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                        <div>
                            <span class="font-semibold">{{ item.name }}</span>
                            <span class="text-gray-600 ml-2">{{ item.price }}€ x {{ item.quantity }}</span>
                        </div>
                        <div class="flex items-center space-x-2">
                            <button
                                    @click="decreaseQuantity(item)"
                                    class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300"
                            >
                                -
                            </button>
                            <span class="w-8 text-center">{{ item.quantity }}</span>
                            <button
                                    @click="increaseQuantity(item)"
                                    class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300"
                            >
                                +
                            </button>
                            <button
                                    @click="removeFromOrder(item)"
                                    class="ml-4 text-red-600 hover:text-red-800"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    <div class="border-t pt-4 mt-4">
                        <div class="flex justify-between items-center text-lg font-semibold">
                            <span>Total:</span>
                            <span>{{ total }}€</span>
                        </div>
                    </div>

                    <div class="flex space-x-4 mt-6">
                        <button
                                @click="clearOrder"
                                class="flex-1 bg-gray-500 text-white py-3 rounded hover:bg-gray-600 transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                                @click="submitOrder"
                                :disabled="currentOrderItems.length === 0"
                                class="flex-1 bg-blue-600 text-white py-3 rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            Valider la commande
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';

    export default {
        name: 'ServeurPriseCommande',
        props: {
            tableId: {
                type: [String, Number],
                required: true
            }
        },
        data() {
            return {
                selectedCategory: 'bar',
                currentOrderItems: [],
                categories: ['bar', 'cuisine']
            };
        },
        computed: {
            ...mapState(['products', 'currentRestaurant', 'tables']),
            ...mapGetters(['currentCashRegister']),
            tableNumber() {
                const table = this.tables.find(t => t.id === parseInt(this.tableId));
                return table ? table.number : 'N/A';
            },
            filteredProducts() {
                return this.products.filter(product => product.type === this.selectedCategory);
            },
            total() {
                return this.currentOrderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            }
        },
        methods: {
            ...mapActions(['createOrder']),
            addToOrder(product) {
                const existingItem = this.currentOrderItems.find(item => item.productId === product.id);
                if (existingItem) {
                    existingItem.quantity++;
                } else {
                    this.currentOrderItems.push({
                        id: Date.now() + Math.random(),
                        productId: product.id,
                        name: product.name,
                        type: product.type,
                        price: product.price,
                        quantity: 1
                    });
                }
            },
            removeFromOrder(item) {
                const index = this.currentOrderItems.indexOf(item);
                if (index > -1) {
                    this.currentOrderItems.splice(index, 1);
                }
            },
            increaseQuantity(item) {
                item.quantity++;
            },
            decreaseQuantity(item) {
                if (item.quantity > 1) {
                    item.quantity--;
                } else {
                    this.removeFromOrder(item);
                }
            },
            clearOrder() {
                this.currentOrderItems = [];
            },
            async submitOrder() {
                try {
                    if (this.currentCashRegister?.status !== 'open') {
                        alert('La caisse doit être ouverte pour prendre une commande');
                        return;
                    }

                    await this.createOrder({
                        tableId: parseInt(this.tableId),
                        items: this.currentOrderItems
                    });

                    this.$router.push('/serveur/tables');
                } catch (error) {
                    alert(error.message);
                }
            }
        }
    };
</script>